import React from 'react';
import { Renovation } from '@search/filter-enums/enums/Renovation';
import { RankingEnum } from '@search/graphql-typings';
import { OfferRoute, useRouter } from '@search/offer-search-routes/src';
import { useGqlContext, GqlClient } from '@search/gql-client/src';
import { gql2CreateCachedFetcher, useGql2Loader } from '@search/gql-client/src/useGql2';
import { useMarketingSend } from '@search/classified-common/src/marketing/useMarketingSend';

import { GeoRootRegionRepository } from '@search/geo/src/GeoRootRegionRepository';
import { useClGeoRootRegion } from '@search/geo/src/clGeoRootRegion/useClGeoRootRegion';
import { ClassifiedAnalytics } from '../../../analytics/ClassifiedAnalytics';
import { OfferQuery$data as OfferQueryResponse, OfferQuery$variables as OfferQueryVariables } from './__generated__/OfferQuery.graphql';
import { OfferQuery } from './OfferQuery';

const cachePrefix = 'Offer';

type OfferQueryVars = Partial<Pick<OfferQueryVariables, 'skipNotMain'>> & Omit<OfferQueryVariables, 'skipNotMain'>;

function createOfferFetcher({ gql, rootRegions }: {
    gql: GqlClient;
    rootRegions?: GeoRootRegionRepository;
}) {
    return async (vars: OfferQueryVars, opts?: { signal?: AbortSignal }) => {
        const res = gql.client<
            OfferQueryVariables,
            OfferQueryResponse
        >(OfferQuery, {
            skipNotMain: false,
            ...vars
        }, opts);

        if (! rootRegions) return res;

        const current = await res;

        const offer = current.data?.offer.__typename === 'ProfOfferSnippet' ? current.data.offer : undefined;

        [
            offer,
            ...offer?.related ?? []
        ].forEach(item => item?.location && rootRegions.slugs.addLocation(item.location));

        return current;
    };
}

export function createOfferCachedFetcher({ gql, userId = '', rootRegions }: {
    gql: GqlClient;
    userId?: string;
    rootRegions?: GeoRootRegionRepository;
}) {
    const fetcher = createOfferFetcher({ gql, rootRegions });

    return gql2CreateCachedFetcher({ fetcher, cachePrefix: cachePrefix + userId, gql });
}

function useOfferFetcher() {
    const gql = useGqlContext();
    const rootRegions = useClGeoRootRegion();

    return React.useCallback(createOfferFetcher({ gql, rootRegions }), [ gql, rootRegions ]);
}

export function useOfferQuery() {
    const loader = useGql2Loader(useOfferFetcher(), { cachePrefix, authTrack: true });
    const marketingSend = useMarketingSend();

    const rootRegions = useClGeoRootRegion();

    return React.useCallback((p: Parameters<typeof loader>[0]) => {
        const result = loader(p);
        const offer = result.data?.offer.__typename === 'ProfOfferSnippet' ? result.data.offer : undefined;

        if (offer?.id) {
            marketingSend({
                type: 'card',
                offerId: offer.id
            });
        }

        [
            offer,
            ...offer?.related ?? []
        ].forEach(item => item?.location && rootRegions.slugs.addLocation(item.location));

        return result;
    }, [ loader, rootRegions ]);
}

export type OfferType = Extract<OfferQueryResponse['offer'], { __typename: 'ProfOfferSnippet' }>;
export type OfferTypeSimply = Omit<
    Extract<OfferQueryResponse['offer'], { __typename: 'ProfOfferSnippet' }>,
    'renovationProgram' | 'buildEpoch' | 'similarNb' | 'buildingSeries' | 'heatingType' | 'infrastructureEntitiesList' | 'yardEntityList' | 'ratedSchools' | '__typename'
>;

export function renovationCompat(realtyObject: OfferType['realtyObject']): Renovation | undefined {
    /* eslint-disable no-nested-ternary */
    const renovationType = realtyObject?.__typename === 'ProfOfferRoom' ?
        realtyObject.enclosingFlat?.characteristics?.renovationType :
        (realtyObject?.__typename === 'ProfOfferFlat' ? realtyObject.characteristics?.renovationType : undefined);

    switch (renovationType) {
        case 'DESIGN': return Renovation.DESIGNED;
        case 'EURO': return Renovation.EURO;
        case 'NEED': return Renovation.NEED;
        case 'GOOD': return Renovation.COSMETIC;
        case 'PARTIAL': return Renovation.NEED;
        case 'PRE_FINE': return Renovation.COSMETIC;
        case 'FINE': return Renovation.COSMETIC;
        case 'ROUGH':
        default:
            return undefined;
    }
}

export function getOfferCardGtmId({
    realtyObject,
    seller,
    dealType,
    status,
    newBuildingCard
}: OfferType) {
    if (dealType?.__typename === 'ProfOfferDealSell') {
        if (
            realtyObject?.__typename === 'ProfOfferFlat' &&
            seller.sellerType === 'DEVELOPER' &&
            realtyObject?.building?.isNewbuilding && (
                newBuildingCard?.ranking === RankingEnum.Paid ||
                newBuildingCard?.ranking === RankingEnum.SpecialProject ||
                newBuildingCard?.ranking === RankingEnum.SuperPaid
            )
        ) {
            return {
                top: ClassifiedAnalytics.SearchCardSaleFromDevPaidShowTel,
                bottom: ClassifiedAnalytics.SearchCardSaleFromDevPaidShowTel
            };
        }

        if (
            realtyObject?.__typename === 'ProfOfferFlat' &&
            seller.sellerType === 'DEVELOPER' &&
            realtyObject?.building?.isNewbuilding
        ) {
            return {
                top: ClassifiedAnalytics.SearchCardSaleFromDevShowTelTop,
                bottom: ClassifiedAnalytics.SearchCardSaleFromDevShowTelBottom
            };
        }

        if (
            realtyObject?.__typename === 'ProfOfferFlat' &&
            realtyObject?.building?.isNewbuilding
        ) {
            return {
                top: ClassifiedAnalytics.SearchCardSaleNewShowTelTop,
                bottom: ClassifiedAnalytics.SearchCardSaleNewShowTelBottom
            };
        }

        if (status?.hasAnyPriority) {
            return {
                top: ClassifiedAnalytics.SearchCardSalePaidShowTelTop,
                bottom: ClassifiedAnalytics.SearchCardSalePaidShowTelBottom
            };
        }

        return {
            top: ClassifiedAnalytics.SearchCardSaleShowTelTop,
            bottom: ClassifiedAnalytics.SearchCardSaleShowTelBottom
        };
    }

    if (dealType?.__typename === 'ProfOfferDealRent') {
        if (
            realtyObject?.__typename === 'ProfOfferFlat' &&
            realtyObject?.building?.isNewbuilding
        ) {
            return {
                top: ClassifiedAnalytics.SearchCardRentNewShowTelTop,
                bottom: ClassifiedAnalytics.SearchCardRentNewShowTelBottom
            };
        }

        return {
            top: ClassifiedAnalytics.SearchCardRentShowTelTop,
            bottom: ClassifiedAnalytics.SearchCardRentShowTelBottom
        };
    }

    return undefined;
}

export type OfferTinySnippetModel = NonNullable<OfferType['buildingRelated']>['list'][0];

export function useTinyOffer(model: OfferTinySnippetModel) {
    const mainImageModel = model.gallery?.mainImage;

    let formattedPrice = '';

    if (model.dealType?.__typename === 'ProfOfferDealRent') {
        formattedPrice = model.dealType.price?.formattedWithCurrency ?? '';
    }
    if (model.dealType?.__typename === 'ProfOfferDealSell') {
        formattedPrice = model.dealType.price?.formatted ?? '';
    }

    const mainImage = {
        originPath: mainImageModel?.originPath,
        description: model.text?.titleCard ?? '',
        attributesType: mainImageModel?.attributesType
    };

    let offerUrl = '';
    const router = useRouter();
    const offerRoute = router.route(OfferRoute);

    if (model.id && model.text?.slug && model?.location.narrowRegion?.id) {
        offerUrl = offerRoute.url({
            offerId: model.id,
            slug: model.text?.slug,
            region: model?.location.narrowRegion?.id
        });
    }

    return {
        offerUrl,
        mainImage,
        formattedPrice,
        titleSnippetMini: model.text?.titleSnippetMini
    };
}
